import { useCallback, useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { EventFlow, FormInput, ListenerConfig } from '@microservices/wiskey-react-components'
import { Listener, useListenerEngine } from '@microservices/wiskey-react-utils'
import { Box, Button, Stack } from '@mui/material'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { getDataFromState } from '@components/CommandParameters/helpers/getDataFromState'
import { CommandPickerController } from '@components/hookFormControllers/CommandPickerController'

import { useExecuteCommandMutation } from '@redux/api'

import { getCommandRequestData } from '@helpers'
import { GENERATOR_INPUT_TYPE } from '@constants'
import { ACTION_CODE, EVENT_CODE } from '@constants'

const ACTION_CODE_OPTIONS = Object.values(ACTION_CODE).map(code => ({
  id: code,
  label: code,
}))

const EVENT_CODE_OPTIONS = Object.values(EVENT_CODE).map(code => ({
  id: code,
  label: code,
}))

export const ListenerConfigTestPage = () => {
  // Упрощенная конфигурация - только один event flow с ON_BUTTON_CLICK
  const [listenerConfig, setListenerConfig] = useState<Listener<EVENT_CODE, ACTION_CODE>>({
    id: 'test_listener',
    name: 'Test Listener',
    entityId: 'test',
    enabled: true,
    eventFlows: [
      {
        id: 'flow_1',
        eventCode: EVENT_CODE.ON_BUTTON_CLICK,
        actions: [
          {
            id: 'action_1',
            actionCode: ACTION_CODE.EXECUTE_COMMAND,
            meta: {
              commands: [
                {
                  code: null,
                  sourceCommand: {
                    id: 'tb_flights',
                    label: 'STD Flights object (table)',
                    typeResource: 'OBJECT',
                    code: 'tb_flights',
                  },
                  objectTitle: null,
                  name: {
                    id: 'object_read_all',
                    label: 'object_read_all',
                    httpMethod: 'GET',
                  },
                  sourceType: {
                    id: 'OBJECT',
                    label: 'OBJECT',
                  },
                  parameters: [
                    // {
                    //   id: null,
                    //   title: {
                    //     id: 'filter',
                    //     label: 'filter',
                    //   },
                    //   type: {
                    //     id: 'STATIC',
                    //     label: 'static',
                    //   },
                    //   objectCode: null,
                    //   value: '_id == 6947141ab0ffa51cee80b695',
                    //   pathArray: null,
                    // },
                  ],
                  body: [],
                  httpMethod: 'GET',
                },
              ],
            },
          },
          { id: 'action_2', actionCode: ACTION_CODE.SET_VARIABLE_VALUE, dependsOn: ['action_1'] },
          { id: 'action_3', actionCode: ACTION_CODE.SET_DATA, dependsOn: ['action_2'] },
        ],
      },
    ],
  })

  // Логирование состояния конфигурации при монтировании
  useEffect(() => {
    console.log('📋 ListenerConfig initialized:', {
      eventFlows: listenerConfig.eventFlows.length,
      totalActions: listenerConfig.eventFlows.reduce((sum, flow) => sum + flow.actions.length, 0),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleEventFlowsChange = useCallback(
    (newEventFlows: EventFlow<EVENT_CODE, ACTION_CODE>[]) => {
      setListenerConfig(prev => ({
        ...prev,
        eventFlows: newEventFlows,
      }))
    },
    []
  )

  const [executeCommand] = useExecuteCommandMutation()

  // Handler для выполнения команды
  const handleExecuteCommand = useCallback(
    async (action: any, context: any) => {
      const command = action.meta?.commands?.[0]
      if (!command) {
        console.warn('No command found in action.meta.commands')

        return { executed: false, actionId: action.id, error: 'No command found' }
      }

      const objectCode = command.sourceCommand?.id || command.sourceCommand?.code
      const commandCode = command.name?.id
      const httpMethod = command.name?.httpMethod || command.httpMethod || 'POST'

      if (!objectCode || !commandCode) {
        console.warn('Missing objectCode or commandCode', { objectCode, commandCode })

        return { executed: false, actionId: action.id, error: 'Missing command data' }
      }

      try {
        // Формируем srcObj из context для использования в параметрах команды
        const srcObj = {
          ...(context.previousResult || {}),
          ...(context.dependencies || {}),
          ...(context.eventData || {}),
        }

        // Формируем executionContext для JS-выражений
        const executionContext = {
          previousResult: context.previousResult,
          dependencies: context.dependencies,
          eventData: context.eventData,
          ...srcObj,
        }

        // Преобразуем parameters и body из формы (с объектами) в формат для API (строки)
        const parametersDTO = getDataFromState(command.parameters)
        const bodyDTO = command.body ? getDataFromState(command.body) : []

        // Преобразуем parameters и body с учетом context
        const params = getCommandRequestData(
          parametersDTO,
          srcObj,
          undefined,
          undefined,
          executionContext
        )

        const body =
          httpMethod !== 'GET' && bodyDTO.length > 0
            ? getCommandRequestData(bodyDTO, srcObj, undefined, undefined, executionContext)
            : undefined

        console.log('🚀 Executing command:', { objectCode, commandCode, httpMethod })

        const result = await executeCommand({
          method: httpMethod,
          objectCode: objectCode as string,
          commandCode: commandCode as string,
          params,
          body,
          isCommandResponse: Boolean(command.isCommandResponse),
        }).unwrap()

        console.log('✅ Command executed successfully')

        return { executed: true, actionId: action.id, result: result?.[0] }
      } catch (error) {
        console.error('❌ Error executing command:', error)

        return { executed: false, actionId: action.id, error }
      }
    },
    [executeCommand]
  )

  // useListenerEngine с простыми handlers
  const { triggerEvent } = useListenerEngine<EVENT_CODE, ACTION_CODE>({
    listener: listenerConfig,
    actionHandlers: {
      [ACTION_CODE.EXECUTE_COMMAND]: handleExecuteCommand,
      [ACTION_CODE.SET_VARIABLE_VALUE]: async (action, context) => {
        console.log('🔧 SET_VARIABLE_VALUE:', action.meta?.variableName)
        await new Promise(resolve => setTimeout(resolve, 300))
        console.log('context', context)
        alert(JSON.stringify(context))

        return { variableSet: true, actionId: action.id }
      },
      [ACTION_CODE.SET_DATA]: async (action, context) => {
        console.log('💾 SET_DATA:', action.meta?.dataPath)
        await new Promise(resolve => setTimeout(resolve, 200))
        alert(JSON.stringify(context))

        return { dataSet: true, actionId: action.id }
      },
    },
  })

  const handleButtonClick = async () => {
    console.log('🔘 Button clicked, triggering ON_BUTTON_CLICK event')
    await triggerEvent(
      EVENT_CODE.ON_BUTTON_CLICK,
      { buttonId: 'test-button' },
      (error, action, context, flowId) => {
        console.error('❌ Error in flow:', flowId)
        console.error('Failed action:', action, error)
      }
    )
    console.log('✅ Event flow completed')
  }

  const renderActionMetaInputs = useCallback((actionCode: ACTION_CODE) => {
    switch (actionCode) {
      case ACTION_CODE.EXECUTE_COMMAND:
        return (
          <CommandPickerController
            isAnotherObjectWithRequestData
            isSingle
            isTypeHidden
            commandName='name'
            name='meta.commands'
            objectCode=''
          />
        )
      case ACTION_CODE.SET_VARIABLE_VALUE:
        return <></>

        return (
          <>
            <FormInput
              inputType={GENERATOR_INPUT_TYPE.INPUT}
              label='Variable Name'
              name='meta.variableName'
            />
            <FormInput
              inputType={GENERATOR_INPUT_TYPE.INPUT}
              label='Variable Value'
              name='meta.variableValue'
            />
          </>
        )
      case ACTION_CODE.SET_DATA:
        return (
          <>
            <FormInput
              inputType={GENERATOR_INPUT_TYPE.INPUT}
              label='Data Path'
              name='meta.dataPath'
            />
            <FormInput
              inputType={GENERATOR_INPUT_TYPE.TEXTAREA}
              label='Data Value'
              name='meta.dataValue'
            />
          </>
        )
      default:
        return null
    }
  }, [])

  const renderEventFlowMetaInputs = useCallback((eventCode: EVENT_CODE) => {
    switch (eventCode) {
      case EVENT_CODE.ON_BUTTON_CLICK:
        return (
          <>
            <FormInput
              inputType={GENERATOR_INPUT_TYPE.INPUT}
              label='Button ID'
              name='meta.buttonId'
              placeholder='Enter button ID'
            />
          </>
        )
      default:
        return null
    }
  }, [])

  const isDev = process.env.NODE_ENV === 'development'

  if (!isDev) {
    return <Navigate replace to='/' />
  }

  return (
    <PageContentLayout>
      <Stack spacing={3} sx={{ p: 2 }}>
        {/* Конфигурация ListenerConfig */}
        <Box>
          <ListenerConfig<EVENT_CODE, ACTION_CODE>
            actionCodeOptions={ACTION_CODE_OPTIONS}
            eventCodeOptions={EVENT_CODE_OPTIONS}
            eventFlows={listenerConfig.eventFlows}
            renderActionMetaInputs={renderActionMetaInputs}
            renderEventFlowMetaInputs={renderEventFlowMetaInputs}
            onChange={handleEventFlowsChange}
          />
        </Box>

        {/* Кнопка для запуска event flow */}
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
          <Button variant='contained' onClick={handleButtonClick}>
            Trigger ON_BUTTON_CLICK Event
          </Button>
        </Box>
      </Stack>
    </PageContentLayout>
  )
}
